import React, {useState, useEffect} from "react";
import {
    Box,
    Text,
    Textarea,
    Button,
    useColorModeValue,
    useToast,
    Flex,
    Spinner,
    Stack
} from "@chakra-ui/react";
import {cqAPI} from "../../lib/Auth";

const VAMPrompts = () => {
    const bgColor = useColorModeValue("gray.100", "gray.700");
    const cardBg = useColorModeValue("#ffffff", "#212330");
    const borderClr = useColorModeValue("gray.200", "gray.600");

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const defaultFormData = {
        general_agent_prompt: "",
        collection_identifier_prompt: "",
        moderator_prompt: "",
        security_prompt: "",
        note_creator_system_prompt: "",
        demo_prompt: "",
        no_pms_data_collected_prompt: "",
        emergency_prompt: "",
        chat_sess_detection_prompt: "",
        chat_sess_summarizer_prompt: "",
        followup_prompt: "",
        general_responder_guidelines_prompt: "",
        verification_missing_data_prompt: "",
        failed_verification_prompt: "",
        appointment_booker_prompt: {base_prompt: "", pms_section: ""},
        appointment_confirmer_prompt: {intro: "", goal: "", personal_info: "", date_section: "", guidelines: ""},
        appointment_canceler_prompt: {
            intro: "",
            reschedule_question: "",
            personal_info: "",
            date_section: "",
            guidelines: ""
        },
        appointment_rescheduler_prompt: {intro: "", personal_info: "", date_section: "", guidelines: ""},
        appointment_manager_prompt: {guidelines: ""},
        data_extractor_prompt: {intro: "", guidelines: ""},
        health_agent_prompt: {intro: "", health_prompt: ""},

    };


    const [formData, setFormData] = useState(defaultFormData);
    const toast = useToast();

    // ----------- LOAD PROMPTS -----------
    useEffect(() => {
        const fetchVAMPrompts = async () => {
            setLoading(true);
            try {
                const response = await cqAPI.get("/prompts/vam");
                console.log("vam repsonse: ", response.data);
                if (response.type === "ok" && response.data) {
                    setFormData(response.data);
                } else {
                    console.warn("Invalid or missing response data, using defaults");
                    setFormData(defaultFormData);
                    toast({
                        title: "Warning",
                        description: "Loaded default VAM Prompts because the API response was invalid.",
                        status: "warning",
                        duration: 3000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                console.error("Error loading VAM Prompt:", error);
                toast({
                    title: "Error",
                    description: "Failed to load VAM Prompt.",
                    status: "error",
                    duration: 3000,
                    isClosable: true
                });
            } finally {
                setLoading(false);
            }
        };
        fetchVAMPrompts();
    }, []);

    // ----------- SAVE PROMPTS -----------
    const handleSave = async () => {
        setSaving(true);
        try {
            console.log(formData)
            const response = await cqAPI.put("/prompts/vam", formData);
            if (response.type === "ok") {
                toast({
                    title: "Saved",
                    description: "VAM Prompts updated successfully.",
                    status: "success",
                    duration: 3000,
                    isClosable: true
                });
            } else {
                toast({
                    title: "Error",
                    description: response.msg || 'Failed to save!',
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error("Error saving VAM Prompt:", error);
            toast({
                title: "Error",
                description: "Failed to save VAM Prompt.",
                status: "error",
                duration: 3000,
                isClosable: true
            });
        } finally {
            setSaving(false);
        }
    };

    // ----------- RENDER UI -----------
    return (
        <Box bg={bgColor} minH="100dvh" w="100%">
            <Box bg={bgColor} p={6} borderRadius="md" w="100%" maxW="100%">
            <Flex justify="space-between" align="center" mb={6}>
                <Text fontSize="xl" fontWeight="bold">
                    VAM Prompts
                </Text>
                <Button
                    colorScheme="blue"
                    onClick={handleSave}
                    isLoading={saving}
                    loadingText="Saving"
                >
                    Save
                </Button>
            </Flex>

            {loading ? (
                <Flex justify="center" align="center" h="200px">
                    <Spinner size="xl"/>
                </Flex>
            ) : (
                <Stack spacing={6}>
                    {/* General Agent Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            General Agent Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.general_agent_prompt}
                            onChange={(e) =>
                                setFormData({...formData, general_agent_prompt: e.target.value})
                            }
                            placeholder="General agent prompt..."
                        />
                    </Box>

                    {/* Collection Identifier Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Collection Identifier Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.collection_identifier_prompt}
                            onChange={(e) =>
                                setFormData({...formData, collection_identifier_prompt: e.target.value})
                            }
                            placeholder="Collection Identifier Prompt..."
                        />
                    </Box>

                    {/* Moderator Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Moderator Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.moderator_prompt}
                            onChange={(e) =>
                                setFormData({...formData, moderator_prompt: e.target.value})
                            }
                            placeholder="Moderator Prompt..."
                        />
                    </Box>

                    {/* Security Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Security Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.security_prompt}
                            onChange={(e) =>
                                setFormData({...formData, security_prompt: e.target.value})
                            }
                            placeholder="Security Prompt..."
                        />
                    </Box>

                    {/* Note Creator System Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Note Creator System Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.note_creator_system_prompt}
                            onChange={(e) =>
                                setFormData({...formData, note_creator_system_prompt: e.target.value})
                            }
                            placeholder="Note Creator System Prompt..."
                        />
                    </Box>

                    {/* Demo Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Demo Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.demo_prompt}
                            onChange={(e) =>
                                setFormData({...formData, demo_prompt: e.target.value})
                            }
                            placeholder="Demo Prompt..."
                        />
                    </Box>

                    {/* No PMS Data Collected Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            No PMS Data Collected Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.no_pms_data_collected_prompt}
                            onChange={(e) =>
                                setFormData({...formData, no_pms_data_collected_prompt: e.target.value})
                            }
                            placeholder="No PMS Data Collected Prompt..."
                        />
                    </Box>

                    {/* Emergency Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Emergency Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.emergency_prompt}
                            onChange={(e) =>
                                setFormData({...formData, emergency_prompt: e.target.value})
                            }
                            placeholder="Emergency Prompt..."
                        />
                    </Box>

                    {/* Chat Session Detection Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Chat Session Detection Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.chat_sess_detection_prompt}
                            onChange={(e) =>
                                setFormData({...formData, chat_sess_detection_prompt: e.target.value})
                            }
                            placeholder="Chat Session Detection Prompt..."
                        />
                    </Box>

                    {/* Chat Session Summarizer Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Chat Session Summarizer Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.chat_sess_summarizer_prompt}
                            onChange={(e) =>
                                setFormData({...formData, chat_sess_summarizer_prompt: e.target.value})
                            }
                            placeholder="Chat Session Summarizer Prompt..."
                        />
                    </Box>

                    {/* Followup Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Followup Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.followup_prompt}
                            onChange={(e) =>
                                setFormData({...formData, followup_prompt: e.target.value})
                            }
                            placeholder="Followup Prompt..."
                        />
                    </Box>

                    {/* General Responder Guidelines Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            General Responder Guidelines Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.general_responder_guidelines_prompt}
                            onChange={(e) =>
                                setFormData({...formData, general_responder_guidelines_prompt: e.target.value})
                            }
                            placeholder="General Responder Guidelines Prompt..."
                        />
                    </Box>

                    {/* Verification missing data Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Verification missing data Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.verification_missing_data_prompt}
                            onChange={(e) =>
                                setFormData({...formData, verification_missing_data_prompt: e.target.value})
                            }
                            placeholder="Verification missing data Prompt..."
                        />
                    </Box>

                    {/* Failed Verification Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Failed Verification Prompt
                        </Text>
                        <Textarea
                            minH="120px"
                            resize="vertical"
                            value={formData.failed_verification_prompt}
                            onChange={(e) =>
                                setFormData({...formData, failed_verification_prompt: e.target.value})
                            }
                            placeholder="Failed Verification Prompt..."
                        />
                    </Box>

                    {/* Appointment Booker Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Appointment Booker Prompt
                        </Text>
                        <Stack spacing={3}>
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_booker_prompt.base_prompt}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_booker_prompt: {
                                            ...formData.appointment_booker_prompt,
                                            base_prompt: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Booker Prompt Base Prompt..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_booker_prompt.pms_section}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_booker_prompt: {
                                            ...formData.appointment_booker_prompt,
                                            pms_section: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Booker Prompt PMS Section..."
                            />
                        </Stack>
                    </Box>

                    {/* Appointment Confirmer Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Appointment Confirmer Prompt
                        </Text>
                        <Stack spacing={3}>
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_confirmer_prompt.intro}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_confirmer_prompt: {
                                            ...formData.appointment_confirmer_prompt,
                                            intro: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Confirmer Prompt Intro..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_confirmer_prompt.goal}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_confirmer_prompt: {
                                            ...formData.appointment_confirmer_prompt,
                                            goal: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Confirmer Prompt Goal..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_confirmer_prompt.personal_info}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_confirmer_prompt: {
                                            ...formData.appointment_confirmer_prompt,
                                            personal_info: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Confirmer Prompt Personal Info..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_confirmer_prompt.date_section}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_confirmer_prompt: {
                                            ...formData.appointment_confirmer_prompt,
                                            date_section: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Confirmer Prompt Date Section..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_confirmer_prompt.guidelines}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_confirmer_prompt: {
                                            ...formData.appointment_confirmer_prompt,
                                            guidelines: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Confirmer Prompt Guidelines..."
                            />
                        </Stack>
                    </Box>

                    {/* Appointment Canceler Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Appointment Canceler Prompt
                        </Text>
                        <Stack spacing={3}>
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_canceler_prompt.intro}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_canceler_prompt: {
                                            ...formData.appointment_canceler_prompt,
                                            intro: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Canceler Prompt Intro..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_canceler_prompt.reschedule_question}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_canceler_prompt: {
                                            ...formData.appointment_canceler_prompt,
                                            reschedule_question: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Canceler Prompt Reschedule Question..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_canceler_prompt.personal_info}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_canceler_prompt: {
                                            ...formData.appointment_canceler_prompt,
                                            personal_info: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Canceler Prompt Personal Info..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_canceler_prompt.date_section}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_canceler_prompt: {
                                            ...formData.appointment_canceler_prompt,
                                            date_section: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Canceler Prompt Date Section..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_canceler_prompt.guidelines}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_canceler_prompt: {
                                            ...formData.appointment_canceler_prompt,
                                            guidelines: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Canceler Prompt Guidelines..."
                            />
                        </Stack>
                    </Box>

                    {/* Appointment Rescheduler Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Appointment Rescheduler Prompt
                        </Text>
                        <Stack spacing={3}>
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_rescheduler_prompt.intro}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_rescheduler_prompt: {
                                            ...formData.appointment_rescheduler_prompt,
                                            intro: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Rescheduler Prompt Intro..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_rescheduler_prompt.personal_info}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_rescheduler_prompt: {
                                            ...formData.appointment_rescheduler_prompt,
                                            personal_info: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Rescheduler Prompt Personal Info..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_rescheduler_prompt.date_section}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_rescheduler_prompt: {
                                            ...formData.appointment_rescheduler_prompt,
                                            date_section: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Rescheduler Prompt Date Section..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_rescheduler_prompt.guidelines}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_rescheduler_prompt: {
                                            ...formData.appointment_rescheduler_prompt,
                                            guidelines: e.target.value,
                                    },
                                    })
                                }
                                placeholder="Appointment Rescheduler Prompt Guidelines..."
                            />
                        </Stack>
                    </Box>

                    {/* Appointment Manager Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Appointment Manager Prompt
                        </Text>
                        <Stack spacing={3}>
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.appointment_manager_prompt.guidelines}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        appointment_manager_prompt: {
                                            ...formData.appointment_manager_prompt,
                                            guidelines: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Appointment Manager Prompt Guideline..."
                            />
                        </Stack>
                    </Box>

                    {/* Data Extractor Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Data Extractor Prompt
                        </Text>
                        <Stack spacing={3}>
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.data_extractor_prompt.intro}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        data_extractor_prompt: {
                                            ...formData.data_extractor_prompt,
                                            intro: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Data extractor Prompt Intro..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.data_extractor_prompt.guidelines}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        data_extractor_prompt: {
                                            ...formData.data_extractor_prompt,
                                            guidelines: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Data Extractor Prompt Guidelines..."
                            />
                        </Stack>
                    </Box>

                    {/* Health Agent Prompt */}
                    <Box bg={cardBg} p={4} borderRadius="md" border="1px solid" borderColor={borderClr}>
                        <Text fontWeight="semibold" mb={2}>
                            Health Agent Prompt
                        </Text>
                        <Stack spacing={3}>
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.health_agent_prompt.intro}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        health_agent_prompt: {
                                            ...formData.health_agent_prompt,
                                            intro: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Health Agent Prompt Intro..."
                            />
                            <Textarea
                                minH="100px"
                                resize="vertical"
                                value={formData.health_agent_prompt.health_prompt}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        health_agent_prompt: {
                                            ...formData.health_agent_prompt,
                                            health_prompt: e.target.value,
                                        },
                                    })
                                }
                                placeholder="Data Extractor Prompt Health Prompt..."
                            />
                        </Stack>
                    </Box>

                </Stack>
            )}
        </Box>
        </Box>

    );
};

export default VAMPrompts;